import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "../styles/noticeboardpage1.module.scss";
import tstyles from "../styles/TeacherResources.module.scss";
import nstyles from "../styles/noticeboard.module.scss";

import { Loader } from "lucide-react";
import ToastMessage from "../../../../utils/toasts/toastMessage";
import {
  createNotice,
  noticeList,
  updateNotice,
} from "../../../../Redux/Teacher_Panel/notice_board/notice.action";
import { BASE_IMAGE_URL } from "../../../../utils/api";
import { toInputDate } from "../../../../utils/utils";

const HEADING_MIN = 3;
const HEADING_MAX = 150;
const DESC_MIN = 10;
const DESC_MAX = 500;

const countWords = (str) =>
  str.trim().length === 0 ? 0 : str.trim().split(/\s+/).length;

const MultiSelectDropdown = ({
  id,
  label,
  options,
  selected,
  onChange,
  disabled,
  placeholder = "Select",
  hasError,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const displayLabel =
    selected.length === 0
      ? placeholder
      : selected.length <= 2
        ? selected.join(", ")
        : `${selected.length} selected`;

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        id={id}
        className={nstyles.formSelect}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          border: hasError ? "1px solid #e11d48" : undefined,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={hasError || undefined}
      >
        <span>{displayLabel}</span>
        <span aria-hidden="true" style={{ marginLeft: 8 }}>
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open && !disabled && (
        <div
          role="listbox"
          aria-label={label}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: "#fff",
            border: "1px solid #d0d5dd",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            maxHeight: 240,
            overflowY: "auto",
            padding: "4px 0",
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: "8px 12px", color: "#888", fontSize: 14 }}>
              No options
            </div>
          ) : (
            options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOption(opt.value)}
                  />
                  {opt.label}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const FieldError = ({ message }) =>
  message ? (
    <p
      role="alert"
      style={{
        color: "#e11d48",
        fontSize: 12,
        marginTop: 4,
        marginBottom: 0,
      }}
    >
      {message}
    </p>
  ) : null;

const colorOptions = [
  { name: "Purple", value: "#6B46C1" },
  { name: "Red", value: "#FF6B6B" },
  { name: "Green", value: "#34D399" },
  { name: "Amber", value: "#F59E0B" },
  { name: "Blue", value: "#60A5FA" },
];

export default function NoticeBoardPage1({
  onCancel,
  onSuccess,
  initialNotice,
}) {
  const dispatch = useDispatch();
  const fileRef = useRef(null);

  useEffect(() => {
    if (initialNotice) {
      setTitle(initialNotice.title || "");
      setDesc(initialNotice.desc || "");
      setCharCount((initialNotice.desc || "").length);

      setEventDate(
        toInputDate(initialNotice.event_date || initialNotice.eventDate),
      );

      setClassDivision(
        initialNotice.className && initialNotice.division
          ? [`${initialNotice.className}-${initialNotice.division}`]
          : [],
      );

      setSubject(
        Array.isArray(initialNotice.subject)
          ? initialNotice.subject
          : initialNotice.subject
            ? [initialNotice.subject]
            : [],
      );

      setImages(
        initialNotice.image
          ? [
              {
                file: null,
                url: `${BASE_IMAGE_URL}/data/uploads/announcements/${initialNotice.image}`,
                existing: true,
              },
            ]
          : [],
      );
    } else {
      setTitle("");
      setDesc("");
      setCharCount(0);
      setEventDate("");
      setClassDivision([]);
      setSubject([]);
      setImages([]);
    }

    setCurrentIndex(0);
    setFieldErrors({
      classDivision: "",
      subject: "",
      eventDate: "",
      heading: "",
      desc: "",
    });
    setFormError("");
  }, [initialNotice]);

  const [charCount, setCharCount] = useState(0);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [classDivision, setClassDivision] = useState([]);
  const [subject, setSubject] = useState([]);
  const [eventDate, setEventDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    classDivision: "",
    subject: "",
    eventDate: "",
    heading: "",
    desc: "",
  });
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [color, setColor] = useState(colorOptions[0].value);
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const teacherDetails = useSelector(
    (state) => state?.ProfileManager?.user_details,
  );
  const classAssignments = useMemo(
    () => teacherDetails?.classes || [],
    [teacherDetails],
  );

  const CLASS_DIVISION_OPTIONS = useMemo(() => {
    const seen = new Map();
    classAssignments.forEach((c) => {
      const value = `${c.grade}-${c.division}`;
      if (!seen.has(value)) {
        seen.set(value, {
          value,
          label: `${c.grade}${c.division}`,
          grade: c.grade,
          division: c.division,
        });
      }
    });
    return Array.from(seen.values());
  }, [classAssignments]);

  const SUBJECT_OPTIONS = useMemo(() => {
    if (classDivision.length === 0) return [];
    const selectedPairs = classDivision.map((v) => v.split("-"));
    const matches = classAssignments.filter((c) =>
      selectedPairs.some(
        ([grade, division]) => c.grade === grade && c.division === division,
      ),
    );
    return [...new Set(matches.flatMap((m) => m.subject || []))];
  }, [classAssignments, classDivision]);

  const AUTOPLAY_INTERVAL = 3000;
  const autoplayRef = useRef(null);
  const isPausedRef = useRef(false);

  useEffect(() => {
    return () => {
      images.forEach((url) => {
        if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
      clearAutoplay();
    };
  }, []);

  useEffect(() => {
    clearAutoplay();
    if (images.length > 1 && !isPausedRef.current) startAutoplay();
    return () => clearAutoplay();
  }, [images.length, currentIndex]);

  function startAutoplay() {
    clearAutoplay();
    autoplayRef.current = setInterval(() => {
      setCurrentIndex((p) => (images.length > 0 ? (p + 1) % images.length : 0));
    }, AUTOPLAY_INTERVAL);
  }
  function clearAutoplay() {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }
  function pauseAutoplay() {
    isPausedRef.current = true;
    clearAutoplay();
  }
  function resumeAutoplay() {
    isPausedRef.current = false;
    if (images.length > 1) startAutoplay();
  }

  const handleEventDateChange = (e) => {
    const value = e.target.value;
    setEventDate(value);
    setFieldErrors((prev) => ({
      ...prev,
      eventDate: value ? "" : prev.eventDate,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // revoke old preview URLs
    images.forEach((img) => {
      if (img.url && img.url.startsWith("blob:")) URL.revokeObjectURL(img.url);
    });

    const next = files.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setImages(next);
    setCurrentIndex(0);
    e.target.value = "";
  };

  const triggerFile = () => fileRef.current?.click();

  const removeImage = (e, idx) => {
    e.preventDefault();
    setImages((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const removed = prev[idx];
      if (removed?.url && removed.url.startsWith("blob:"))
        URL.revokeObjectURL(removed.url);
      const next = prev.filter((_, i) => i !== idx);
      setCurrentIndex((cur) => {
        if (next.length === 0) return 0;
        if (idx === cur) return cur >= next.length ? next.length - 1 : cur;
        if (idx < cur) return cur - 1;
        return cur;
      });
      return next;
    });
  };
  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.url && img.url.startsWith("blob:"))
          URL.revokeObjectURL(img.url);
      });
      clearAutoplay();
    };
  }, []);

  const nextSlide = () => {
    if (images.length <= 1) return;
    setCurrentIndex((p) => (p + 1) % images.length);
    if (!isPausedRef.current) {
      clearAutoplay();
      startAutoplay();
    }
  };
  const prevSlide = () => {
    if (images.length <= 1) return;
    setCurrentIndex((p) => (p - 1 + images.length) % images.length);
    if (!isPausedRef.current) {
      clearAutoplay();
      startAutoplay();
    }
  };
  const goToSlide = (e, i) => {
    e.preventDefault();
    if (i < 0 || i >= images.length) return;
    setCurrentIndex(i);
    if (!isPausedRef.current) {
      clearAutoplay();
      startAutoplay();
    }
  };

  const handleClassDivisionChange = (values) => {
    setClassDivision(values);
    const selectedPairs = values.map((v) => v.split("-"));
    const validSubjects = classAssignments
      .filter((c) =>
        selectedPairs.some(
          ([grade, division]) => c.grade === grade && c.division === division,
        ),
      )
      .flatMap((c) => c.subject || []);
    setSubject((prev) => prev.filter((s) => validSubjects.includes(s)));
    setFieldErrors((prev) => ({
      ...prev,
      classDivision: values.length > 0 ? "" : prev.classDivision,
    }));
  };

  const handleSubjectChange = (values) => {
    setSubject(values);
    setFieldErrors((prev) => ({
      ...prev,
      subject: values.length > 0 ? "" : prev.subject,
    }));
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    setFieldErrors((prev) => {
      if (!prev.heading) return prev;
      const trimmed = value.trim();
      const stillInvalid =
        trimmed.length < HEADING_MIN || trimmed.length > HEADING_MAX;
      return stillInvalid ? prev : { ...prev, heading: "" };
    });
  };

  const handleDescChange = (e) => {
    const value = e.target.value;

    setDesc(value);
    setCharCount(value.length);

    setFieldErrors((prev) => {
      if (!prev.desc) return prev;

      const trimmed = value.trim();
      const stillInvalid = trimmed.length < DESC_MIN;

      return stillInvalid ? prev : { ...prev, desc: "" };
    });
  };

  const validateForm = () => {
    const errors = { classDivision: "", subject: "", heading: "", desc: "" };

    if (classDivision.length === 0)
      errors.classDivision = "Select at least one class.";
    if (subject.length === 0) errors.subject = "Select at least one subject.";

    const trimmedHeading = title.trim();
    if (!trimmedHeading) errors.heading = "Title is required.";
    else if (trimmedHeading.length < HEADING_MIN)
      errors.heading = `Heading must be at least ${HEADING_MIN} characters.`;
    else if (trimmedHeading.length > HEADING_MAX)
      errors.heading = `Heading cannot exceed ${HEADING_MAX} characters.`;

    if (!eventDate) errors.eventDate = "Event date is required.";

    const trimmedDesc = desc.trim();
    if (!trimmedDesc) errors.desc = "Description is required.";
    else if (trimmedDesc.length < DESC_MIN)
      errors.desc = `Description must be at least ${DESC_MIN} characters.`;
    else if (trimmedDesc.length > DESC_MAX)
      errors.desc = `Description cannot exceed ${DESC_MAX} characters.`;

    const isValid = Object.values(errors).every((msg) => !msg);
    return { errors, isValid };
  };

  // const handlePost = async () => {
  //   const { errors, isValid } = validateForm();
  //   setFieldErrors(errors);

  //   if (!isValid) {
  //     setFormError("Please fix the highlighted fields before publishing.");
  //     return;
  //   }

  //   setFormError("");
  //   setLoading(true);

  //   const classes = classDivision.map((combo) => {
  //     const [grade, division] = combo.split("-");
  //     const matchingAssignment = classAssignments.find(
  //       (c) => c.grade === grade && c.division === division,
  //     );
  //     const availableSubjects = matchingAssignment?.subject || [];
  //     const subjectsForThisClass = subject.filter((s) =>
  //       availableSubjects.includes(s),
  //     );
  //     return { grade, division, subject: subjectsForThisClass };
  //   });

  //   const payload = {
  //     classes,
  //     description: desc.trim(),
  //     title: title.trim(),
  //     event_date: eventDate,
  //     image: images.length ? images[0].file : null, // real File object
  //   };

  //   try {
  //     const result = await dispatch(createNotice(payload));

  //     if (result.success) {
  //       dispatch(noticeList());
  //       onSuccess?.("Notice published successfully.");
  //     } else {
  //       setToastState({
  //         show: true,
  //         message: result.message || "Something went wrong! Try again.",
  //         type: "error",
  //       });
  //     }
  //   } catch (err) {
  //     setToastState({
  //       show: true,
  //       message: "Something went wrong! Try again.",
  //       type: "error",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const isEditMode = Boolean(initialNotice?.id);

  const handlePost = async () => {
    const { errors, isValid } = validateForm();
    setFieldErrors(errors);

    if (!isValid) {
      setFormError("Please fix the highlighted fields before publishing.");
      return;
    }

    setFormError("");
    setLoading(true);

    const classes = classDivision.map((combo) => {
      const [grade, division] = combo.split("-");
      const matchingAssignment = classAssignments.find(
        (c) => c.grade === grade && c.division === division,
      );
      const availableSubjects = matchingAssignment?.subject || [];
      const subjectsForThisClass = subject.filter((s) =>
        availableSubjects.includes(s),
      );
      return { grade, division, subject: subjectsForThisClass };
    });

    const payload = {
      classes,
      description: desc.trim(),
      title: title.trim(),
      event_date: eventDate,
      image: images.length ? images[0].file : null,
      // id: initialNotice.id,
      ...(initialNotice?.id && { id: initialNotice.id }),
    };

    try {
      const result = isEditMode
        ? await dispatch(updateNotice(payload))
        : await dispatch(createNotice(payload));

      if (result.success) {
        dispatch(noticeList());
        onSuccess?.(
          isEditMode
            ? "Notice updated successfully."
            : "Notice published successfully.",
        );
      } else {
        setToastState({
          show: true,
          message: result.message || "Something went wrong! Try again.",
          type: "error",
        });
      }
    } catch (err) {
      setToastState({
        show: true,
        message: "Something went wrong! Try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const previewImage = images.length ? images[currentIndex].url : null;

  return (
    <div>
      <button
        type="button"
        onClick={onCancel}
        className={styles.postButton}
        style={{ width: "auto", padding: "6px 16px", marginTop: "10px" }}
      >
        ← Back
      </button>
      {/* </div> */}

      <div className={styles.canvas}>
        <div className={styles.createNotice}>
          <div className={styles.createInner}>
            {/* <h2 className={styles.createHeading}>Create Notice</h2> */}
            <h2 className={styles.createHeading}>
              {isEditMode ? "Edit Notice" : "Create Notice"}
            </h2>

            <label className={styles.fieldLabel}>Class</label>
            <MultiSelectDropdown
              id="classDivision"
              label="Class"
              placeholder="Select Class"
              options={CLASS_DIVISION_OPTIONS}
              selected={classDivision}
              onChange={handleClassDivisionChange}
              disabled={CLASS_DIVISION_OPTIONS.length === 0}
              hasError={!!fieldErrors.classDivision}
            />
            <FieldError message={fieldErrors.classDivision} />

            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label className={styles.fieldLabel}>Subject</label>
                <MultiSelectDropdown
                  id="subject"
                  label="Subject"
                  placeholder="Select Subject"
                  options={SUBJECT_OPTIONS.map((s) => ({ value: s, label: s }))}
                  selected={subject}
                  onChange={handleSubjectChange}
                  disabled={
                    classDivision.length === 0 || SUBJECT_OPTIONS.length === 0
                  }
                  hasError={!!fieldErrors.subject}
                />
                <FieldError message={fieldErrors.subject} />
              </div>

              <div style={{ flex: 1 }}>
                <label className={styles.fieldLabel}>Date</label>
                <input
                  type="date"
                  className={nstyles.formSelect}
                  value={eventDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={handleEventDateChange}
                  aria-label="event date"
                  style={{
                    width: "100%",
                    border: fieldErrors.eventDate
                      ? "1px solid #e11d48"
                      : undefined,
                  }}
                />
                <FieldError message={fieldErrors.eventDate} />
              </div>
            </div>

            <label className={styles.fieldLabel}>Title</label>
            <div className={styles.titelBox}>
              <input
                className={styles.textInput}
                value={title}
                onChange={handleTitleChange}
                maxLength={HEADING_MAX}
                aria-label="title"
              />
            </div>
            <FieldError message={fieldErrors.heading} />

            <label className={styles.fieldLabel}>Description</label>
            <div className={styles.discertionBox}>
              <textarea
                className={styles.textArea}
                value={desc}
                onChange={handleDescChange}
                maxLength={DESC_MAX}
                aria-label="description"
              />
            </div>
            {/* <FieldError message={fieldErrors.desc} /> */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <FieldError message={fieldErrors.desc} />

              <span
                style={{
                  fontSize: 12,
                  color: "#888",
                }}
              >
                {charCount}/{DESC_MAX} characters
              </span>
            </div>

            {/* <label className={styles.fieldLabel}>Color Scheme</label>
            <div className={styles.colorSchema}>
              <div className={styles.colorItem}>
                <div className={styles.colorSwatchLarge} style={{ backgroundColor: color }} />
                <div className={styles.colorTextSmall}>
                  {colorOptions.find((c) => c.value === color)?.name ?? "Custom"}
                </div>
              </div>
              <select
                className={styles.colorSelect}
                value={color}
                onChange={(e) => setColor(e.target.value)}
                aria-label="color"
              >
                {colorOptions.map((c) => (
                  <option key={c.value} value={c.value}>{c.name}</option>
                ))}
              </select>
            </div> */}

            <label className={styles.fieldLabel}>Upload Image</label>
            <div className={styles.uploadBox}>
              {images.length === 0 ? (
                <div className={styles.uploadInner} onClick={triggerFile}>
                  <div className={styles.uploadIcon}>📷</div>
                  <div className={styles.uploadText}>upload</div>
                  <div className={styles.uploadHintSmall}>PNG, JPG, GIF</div>
                </div>
              ) : (
                <div className={styles.uploadThumbsWrap}>
                  <div className={styles.uploadThumb}>
                    {images.map((img, idx) => (
                      <div key={img.url} className={styles.thumbItem}>
                        <img
                          src={img.url}
                          alt={`thumb-${idx + 1}`}
                          className={styles.thumbImage}
                          onClick={() => setCurrentIndex(idx)}
                          draggable={false}
                        />
                        <button
                          className={styles.thumbRemoveBtn}
                          onClick={(e) => removeImage(e, idx)}
                          aria-label={`remove-${idx + 1}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className={styles.hiddenFile}
              />
            </div>
          </div>
          <div className={styles.createBorder} aria-hidden />
        </div>

        <div className={styles.previewWrap}>
          {/* <div className={styles.previewHeader}>Preview</div> */}
          <div
            className={styles.previewHeader}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              // gap: 12,
            }}
          >
            <span>Preview</span>
            {/* <button
              className={styles.postButton}
              onClick={handlePost}
              disabled={loading}
            >
              {loading ? "Publishing..." : "Post"}
            </button> */}
            <button
              className={styles.postButton}
              onClick={handlePost}
              disabled={loading}
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Publishing..."
                : isEditMode
                  ? "Update"
                  : "Post"}
            </button>
          </div>
          <div className={styles.previewCard}>
            <div className={styles.previewTop}>
              {/* <span
                className={styles.previewPin}
                style={{ backgroundColor: color }}
                aria-hidden
              /> */}
              <h3 className={styles.previewTitle}>
                {title || "Untitled notice"}
              </h3>
            </div>
            <div className={styles.previewParagraph}>{desc}</div>
            <div
              className={styles.previewImageArea}
              onMouseEnter={pauseAutoplay}
              onMouseLeave={resumeAutoplay}
              onTouchStart={() => {
                isPausedRef.current = true;
                pauseAutoplay();
              }}
              onTouchEnd={() => {
                isPausedRef.current = false;
                resumeAutoplay();
              }}
              aria-label="carousel"
            >
              {previewImage ? (
                // <img
                //   src={previewImage}
                //   alt="preview"
                //   className={styles.previewImage}
                //   draggable={false}
                // />
                <div className={styles.uploadThumbsWrap}>
                  <div className={styles.uploadThumb}>
                    {images.map((img, idx) => (
                      <div key={img.url} className={styles.thumbItem}>
                        <img
                          src={previewImage}
                          alt="preview"
                          className={styles.previewImage}
                          draggable={false}
                        />
                        <button
                          className={styles.thumbRemoveBtn}
                          onClick={(e) => removeImage(e, idx)}
                          aria-label={`remove-${idx + 1}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.previewPlaceholderSmall}>
                  No image selected
                </div>
              )}
              {images.length > 1 && (
                <div className={styles.carouselControlsBottom}>
                  <button
                    className={styles.carouselBtnSmall}
                    onClick={prevSlide}
                    aria-label="prev"
                  >
                    ‹
                  </button>
                  <div className={styles.carouselDotsCentered}>
                    {images.map((_, i) => (
                      <button
                        key={i}
                        className={`${styles.dot} ${i === currentIndex ? styles.activeDot : ""}`}
                        onClick={(e) => goToSlide(e, i)}
                        aria-label={`go-${i}`}
                      />
                    ))}
                  </div>
                  <button
                    className={styles.carouselBtnSmall}
                    onClick={nextSlide}
                    aria-label="next"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Loader show={loading} />
      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
}
